import { useMemo, useRef } from "react";

// Package Imports
import { ColDef, SideBarDef } from "ag-grid-community";
import dayjs from "dayjs";

// Component Imports
import { AlgoStatusCell, StrategyIdCell } from "../components";

// Types Imports
import { IAGAlgoDataRow, NEW_STRATEGY_PLACEHOLDER, useAGTableProps } from "../AlgoTable.types";
import { AgGridReact } from "ag-grid-react";

export const useAGTable = ({
    dataMap,
}: useAGTableProps) => {
    const gridRef = useRef<AgGridReact<IAGAlgoDataRow> | null>(null);

    const rowData = useMemo(() => {
        const entries = Array.from(dataMap.entries());
        entries.sort(([a], [b]) => a.localeCompare(b));
        // Append the "Start a new strategy" placeholder as the last row so it's
        // always available without crowding the working strategies above.
        return [...entries.map(([, v]) => v), NEW_STRATEGY_PLACEHOLDER];
    }, [dataMap]);

    const colDefs = useMemo<ColDef<IAGAlgoDataRow>[]>(
        () => [
            {
                headerName: "Strategy Instance ID",
                field: "strategy_instance_id",
                cellClass: "ag-left-aligned-cell",
                cellRenderer: "agGroupCellRenderer",
                cellRendererParams: {
                    innerRenderer: StrategyIdCell,
                },
            },
            {
                headerName: "Type",
                field: "type",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Connection ID",
                field: "connection_id",
                cellClass: "ag-left-aligned-cell",
                hide: true,
            },
            {
                headerName: "Portfolio",
                field: "portfolio",
                cellClass: "ag-left-aligned-cell",
                hide: true,
            },
            {
                headerName: "P&L",
                field: "pnl",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Status",
                field: "status",
                cellClass: "ag-center-aligned-cell",
                cellRenderer: AlgoStatusCell,
                cellRendererParams: (params: any) => ({
                    value: params.value,
                }),
            },
            {
                headerName: "CPU Usage",
                field: "cpu_usage",
                cellClass: "ag-center-aligned-cell",
                hide: true,
            },
            {
                headerName: "RAM Usage",
                field: "ram_usage",
                cellClass: "ag-center-aligned-cell",
                hide: true,
            },
            {
                headerName: "Strategy",
                field: "strategy",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Created At",
                field: "create_time",
                cellClass: "ag-center-aligned-cell",
                valueFormatter: (params: any) =>
                    params.value ? dayjs(params.value).format("MM/DD/YYYY HH:mm:ss") : "",
            },
            {
                headerName: "Updated At",
                field: "update_time",
                cellClass: "ag-center-aligned-cell",
                valueFormatter: (params: any) =>
                    params.value ? dayjs(params.value).format("MM/DD/YYYY HH:mm:ss") : "",
            },
            {
                headerName: "Accounts",
                field: "accounts",
                cellClass: "ag-left-aligned-cell",
                hide: true,
                valueFormatter: (params: any) =>
                    Array.isArray(params.value) ? params.value.join(", ") : "",
            },
        ],
        []
    );

    const defaultColDef = useMemo<ColDef>(() => ({
        flex: 1,
        filter: true,
        sortable: true,
    }), []);

    const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(
        () => ({ toolPanels: ["columns", "filters"] }),
        []
    );

    return {
        gridRef,
        rowData,
        colDefs,
        defaultColDef,
        sideBar,
    };
}
